import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { badgesApi, type BadgeApi } from '../../api/client';
import styles from './Badges.module.css';

interface UserBadgeStats {
  spotCount: number;
  garageCount: number;
  earnedBadges: BadgeApi[];
  topBadges: BadgeApi[];
}

interface Definitions {
  spotter: BadgeApi[];
  garage: BadgeApi[];
}

function BadgeRow({
  def,
  current,
  earned,
}: {
  def: BadgeApi;
  current: number;
  earned: boolean;
}) {
  const progress = earned ? 100 : Math.min(100, (current / def.requirement) * 100);
  const nextLabel = earned ? 'Earned' : `${current} / ${def.requirement}`;

  return (
    <div className={styles.badgeRow} data-earned={earned} data-tier={def.tier}>
      <div className={styles.badgeInfo}>
        <span className={styles.badgeName}>{def.name}</span>
        <span className={styles.badgeReq}>{def.requirement.toLocaleString()} required</span>
      </div>
      <div className={styles.badgeProgressWrap}>
        <div className={styles.badgeProgressBar} style={{ width: `${progress}%` }} />
        <span className={styles.badgeProgressLabel}>{nextLabel}</span>
      </div>
    </div>
  );
}

export function Badges() {
  const { user, isAuthenticated } = useAuth();
  const [stats, setStats] = useState<UserBadgeStats | null>(null);
  const [definitions, setDefinitions] = useState<Definitions | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isAuthenticated || !user) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    Promise.all([badgesApi.getForUser(user.id), badgesApi.getDefinitions()])
      .then(([s, d]) => {
        if (!cancelled) {
          setStats(s);
          setDefinitions(d);
        }
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load badges');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, user]);

  if (!isAuthenticated) {
    return (
      <div className={styles.page}>
        <h1>Badges</h1>
        <p className={styles.signInPrompt}>Sign in to view and earn badges.</p>
        <Link to="/login" className={styles.cta}>
          Sign in
        </Link>
      </div>
    );
  }

  if (loading) {
    return (
      <div className={styles.page}>
        <h1>Badges</h1>
        <p className={styles.subtitle}>Loading…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.page}>
        <h1>Badges</h1>
        <p className={styles.error}>{error}</p>
      </div>
    );
  }

  const spotCount = stats?.spotCount ?? 0;
  const garageCount = stats?.garageCount ?? 0;
  const earnedIds = new Set((stats?.earnedBadges ?? []).map((b) => b.id));

  return (
    <div className={styles.page}>
      <h1>Badges</h1>
      <p className={styles.subtitle}>
        Earn badges by spotting cars and building your garage. Your top badges appear on your posts.
      </p>

      <div className={styles.stats}>
        <div className={styles.stat}>
          <span className={styles.statValue}>{spotCount.toLocaleString()}</span>
          <span className={styles.statLabel}>Cars spotted</span>
        </div>
        <div className={styles.stat}>
          <span className={styles.statValue}>{garageCount.toLocaleString()}</span>
          <span className={styles.statLabel}>Garage cars</span>
        </div>
      </div>

      {definitions && (
        <>
          <section className={styles.section}>
            <h2>📸 Spotter Badges</h2>
            <p className={styles.sectionDesc}>Earned by uploading spotted cars</p>
            <div className={styles.badgeList}>
              {definitions.spotter.map((def) => (
                <BadgeRow
                  key={def.id}
                  def={def}
                  current={spotCount}
                  earned={earnedIds.has(def.id)}
                />
              ))}
            </div>
          </section>

          <section className={styles.section}>
            <h2>🏎️ Garage Badges</h2>
            <p className={styles.sectionDesc}>Earned by adding cars to your garage</p>
            <div className={styles.badgeList}>
              {definitions.garage.map((def) => (
                <BadgeRow
                  key={def.id}
                  def={def}
                  current={garageCount}
                  earned={earnedIds.has(def.id)}
                />
              ))}
            </div>
          </section>
        </>
      )}
    </div>
  );
}
